/* ============================================
   STICKY PIN (MeshPlanet) — JS-driven Ralph Lauren reveal
   PURPOSE: Pins a `.mp-sticky-group` to a fixed spot in the viewport (top or
            bottom) while its taller parent group (the "track", with the image)
            scrolls behind it. Emulates position:sticky with `transform`
            (GPU, no reflow) so it works regardless of Horizon's height/flex
            quirks that break native sticky.

   WIRING (set by blocks/mp-sticky-group.liquid):
     - Element has classes `mp-sticky-group mp-parallax` and `mp-pin-top`
       OR `mp-pin-bottom`.
     - CSS var on the element: `--mp-parallax-top` or `--mp-parallax-bottom`
       (px offset from that edge of the viewport).

   HOW TO REVERT: disable "Sticky reveal effect" on the block (or delete this
   file + its <script> include in blocks/mp-sticky-group.liquid).
   ============================================ */
(function () {
  if (window.__mpStickyPinInit) return;
  window.__mpStickyPinInit = true;

  var items = [];
  var ticking = false;

  function scrollTop() {
    return window.scrollY || window.pageYOffset || 0;
  }

  // Measure the element's natural geometry WITHOUT its transform, so the math
  // is always relative to the untransformed flow position.
  function measure(it) {
    var el = it.el;
    var prev = el.style.transform;
    el.style.transform = 'none';
    var s = scrollTop();
    var r = el.getBoundingClientRect();
    it.elTop = r.top + s;
    it.elH = el.offsetHeight;
    var tr = it.track.getBoundingClientRect();
    it.trackTop = tr.top + s;
    it.trackH = it.track.offsetHeight;
    el.style.transform = prev;
  }

  function update() {
    ticking = false;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var s = scrollTop();
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      // Viewport Y where the element's TOP should sit while pinned.
      var pinTop;
      if (it.mode === 'center-bottom') {
        // Start centered in the viewport, drift to the bottom as the track
        // scrolls through. p = scroll progress within the pinned range [0..1].
        var pinCenter = (vh - it.elH) / 2;
        var pinBottom = vh - it.offset - it.elH;
        var range = it.trackH - vh;
        var p = range > 0 ? (s - it.trackTop) / range : 0;
        p = p < 0 ? 0 : (p > 1 ? 1 : p);
        pinTop = pinCenter + (pinBottom - pinCenter) * p;
      } else if (it.mode === 'bottom') {
        pinTop = vh - it.offset - it.elH;
      } else {
        pinTop = it.offset;
      }
      var want = s + pinTop - it.elTop;
      // Constrain inside the track: never above its top (plus the top limit),
      // never below its bottom.
      var min = it.trackTop - it.elTop + it.startOffset;
      var max = (it.trackTop + it.trackH) - it.elH - it.elTop;
      if (max < min) max = min;
      var t = want < min ? min : (want > max ? max : want);
      it.el.style.transform = 'translate3d(0,' + t.toFixed(1) + 'px,0)';
    }
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }

  // Re-collect (re-evaluates the mobile/desktop breakpoint) and reposition.
  // Used on resize so crossing the breakpoint engages/disengages the pin.
  function refresh() {
    collect();
    update();
  }

  // Desktop-only breakpoint. Matches the CSS (forced track height applies on
  // min-width:750px). On mobile we skip the pin entirely so the image keeps its
  // natural aspect ratio instead of being stretched to a tall track.
  var DESKTOP_MIN = 750;

  function collect() {
    items = [];
    var els = document.querySelectorAll('.mp-sticky-group.mp-parallax');
    // Always clear any leftover transform first (e.g. when resizing down to
    // mobile, or for reduced-motion users) so nothing stays mispositioned.
    for (var c = 0; c < els.length; c++) els[c].style.transform = '';
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if ((window.innerWidth || document.documentElement.clientWidth) < DESKTOP_MIN) return;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      // Track = the parent group (the one holding the background image).
      var content = el.parentElement; // .group-block-content
      var track = content ? content.closest('.group-block') : null;
      if (!track || track === el) track = el.parentElement;
      if (!track) continue;
      var mode = 'top';
      if (el.classList.contains('mp-pin-center-bottom')) mode = 'center-bottom';
      else if (el.classList.contains('mp-pin-bottom')) mode = 'bottom';
      var cs = getComputedStyle(el);
      var varName = mode === 'top' ? '--mp-parallax-top' : '--mp-parallax-bottom';
      var offset = parseFloat(cs.getPropertyValue(varName)) || 0;
      // Top limit: how far from the track's top edge the pin may travel up.
      var startOffset = parseFloat(cs.getPropertyValue('--mp-sticky-start')) || 0;
      var it = { el: el, track: track, mode: mode, offset: offset, startOffset: startOffset };
      measure(it);
      items.push(it);
    }
  }

  var bound = false;
  function setup() {
    collect();
    // Bind listeners once, even if there are no items yet (mobile / not found),
    // so a later resize to desktop can engage the pin.
    if (!bound) {
      bound = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', refresh, { passive: true });
      // Late-loading images change heights — re-measure once everything loads.
      window.addEventListener('load', refresh);
    }
    update();
  }

  function init() {
    setup();
    // Theme editor: re-collect when a section is re-rendered while editing.
    document.addEventListener('shopify:section:load', setup);
    document.addEventListener('shopify:section:unload', setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
